import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import styles from "./GoLiveWizard.module.css";
import {
  API_BASE_URL,
  getAgentById,
  fetchAvailablePhoneNumberByCountry,
  fetchAvailableVobizNumbers,
  updateAgent,
  calenderCheck,
} from "../../Store/apiStore";
import { isAppSumoUser, isPythonBackend, usesVobiz } from "../../utils/apiBackend";
import { useAgentStore } from "../../Store/agentDetailStore";
import { markSetupStep, fetchSetupScore } from "../../lib/setupScore";
import decodeToken from "../../lib/decodeToken";
import { trackEvent, firePendingPurchase } from "../../lib/analytics";
import {
  isAddonOAuthReturnPending,
  consumeAddonOAuthReturn,
  advanceFromKey,
  beginAddonSequence,
} from "../../lib/addonIntroFlow";
import { readStoredConditions } from "../../lib/callForwardingCodes";
import useStepBackGuard from "../../hooks/useStepBackGuard";
import ExitConfirmModal from "../ExitConfirmModal/ExitConfirmModal";

import ImmediateOwnershipStep from "../PostPurchaseFlow/steps/ImmediateOwnershipStep";
import CarrierSelectStep from "../PostPurchaseFlow/steps/CarrierSelectStep";
import ForwardingStep from "./steps/ForwardingStep";
import TriageStep from "./steps/TriageStep";
import TopStepper from "./TopStepper";

import GoogleAuthStep from "../PostPurchaseFlow/steps/GoogleAuthStep";
import { getVariant, EXPERIMENTS, VARIANT_B } from "../../lib/experiments";
import useBackTo from "../../lib/useBackTo";
import { requestDeviceCarrier } from "../../lib/deviceCarrier";


const STEPS = [
  { id: 1, key: "assign", label: "Assign Number", visualStep: 1, Component: ImmediateOwnershipStep },
  { id: 2, key: "carrier", label: "Carrier", visualStep: 2, Component: CarrierSelectStep },
  { id: 3, key: "forward", label: "Forwarding", visualStep: 2, Component: ForwardingStep },
  { id: 4, key: "triage", label: "Triage", visualStep: 3, Component: TriageStep },
];


const VISUAL_FIRST_INTERNAL = { 1: 1, 2: 2, 3: 4 };

/*
 * Which internal step ENDS the Setup Ready "feature" a step belongs to.
 *
 * Setup Ready shows one card per feature; the wizard runs a 4-step ladder in
 * which the Forwarding feature spans TWO steps (Carrier → Forwarding codes).
 * Only a move OUT of a feature is a place where the "nothing left to do" exit
 * can apply — step 2 → 3 is inside one feature and must always advance.
 *
 *   phoneNumber     → step 1
 *   callForwarding  → steps 2, 3   (ends at 3)
 *   callTriage      → step 4
 */
const FEATURE_END_STEP = { 1: 1, 2: 3, 3: 3, 4: 4 };


const AUTO_ASSIGN_COUNTRIES = new Set(["US", "CA", "IN"]);
const IN_DEFAULT_LOCATION = { city: "Bangalore", stateCode: "KA" };

const STORAGE_KEY = "goLiveWizard.lastStep";

const PAYG_ACTIVE_KEY = "goLiveWizard.paygActive";

// A/B variant B: user ne "How Customers Reach <agent>" pe rasta chun liya?
// SetupPath.js ise set karta hai — dono jagah same string honi chahiye.
const SETUP_PATH_CHOSEN_KEY = "bflow.setupPathChosen";

export default function GoLiveWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [stepId, setStepId] = useState(1);
  const [bootChecked, setBootChecked] = useState(false);
  const initialStepRef = useRef(null);

  const isVariantB = getVariant(EXPERIMENTS.GO_LIVE_FLOW) === VARIANT_B;
  const isFromSetupReady =
    location?.state?.returnTo === "/setup-ready" ||
    sessionStorage.getItem("setupReady.suppressSetupSummary") === "true" ||
    sessionStorage.getItem("setupReady.returnTo") === "/setup-ready";
  const isVariantBFromSetupReady = isVariantB && isFromSetupReady;

  /*
   * ── Setup Ready: stop the ladder once nothing downstream is pending ──
   *
   * Entering a feature from /setup-ready used to run the ladder to its end
   * regardless. Finishing (or skipping) Call Forwarding advanced to Triage, and
   * Triage handed off to the add-on sequence — so a user who only wanted to fix
   * forwarding was walked through features they had already configured.
   *
   * `null` = not resolved yet. Every reader treats "unknown" as PENDING, so the
   * ladder behaves exactly as it does today until we positively know a step is
   * done. Skipping a screen the user still needs is the bad failure; showing one
   * they don't is the safe one.
   *
   * Same sources /setup-ready itself derives its cards from, so the wizard's
   * decision and the card the user tapped cannot disagree.
   */
  const [setupReadyDone, setSetupReadyDone] = useState(null);
  useEffect(() => {
    if (!isFromSetupReady) return undefined;
    let cancelled = false;
    (async () => {
      const agentId =
        sessionStorage.getItem("SelectAgentId") ||
        new URLSearchParams(window.location.search).get("agentId") ||
        null;
      if (!agentId) return;
      try {
        const score = await fetchSetupScore(agentId);
        if (cancelled || !score) return;
        setSetupReadyDone({
          forwarding: !!score?.steps?.callForwardingDone?.done,
          triage: !!score?.steps?.triageRulesSaved?.done,
        });
      } catch (err) {
        // Unknown → every step stays "pending" → ladder unchanged.
        console.warn(
          "[GoLiveWizard] setup score unavailable, running the full ladder:",
          err?.message || err,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromSetupReady]);

  // "When Call Forwarding?" selection — set on the Carrier step, consumed by
  // the Forwarding step. Seeded from sessionStorage so a reload mid-wizard
  // still renders the right dial codes.
  const [forwardingConditions, setForwardingConditions] = useState(() =>
    readStoredConditions(),
  );

  useEffect(() => {
    trackEvent("go_live_start");
  }, []);
  
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);

  const autoAssignTriedRef = useRef(null);
  
  const [hasTriedAutoAssign, setHasTriedAutoAssign] = useState(false);


  const paygActivationRanRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const agentId =
      sessionStorage.getItem("SelectAgentId") ||
      localStorage.getItem("postPurchaseAgentId") ||
      params.get("agentId") ||
      null;

    if (
      params.get("trial") === "1" ||
      sessionStorage.getItem("selectedPlan")?.toLowerCase()?.includes("trial") ||
      sessionStorage.getItem("selectedPlan")?.toLowerCase()?.includes("21-day")
    ) {
      if (agentId) {
        try {
          sessionStorage.setItem("goLiveWizard.trialActive", agentId);
          sessionStorage.setItem("isTrial", "true");
        } catch (_e) {}
      }
    }

    if (params.get("ispayg") !== "true") return;
    if (paygActivationRanRef.current) return;
    paygActivationRanRef.current = true;

    const token = localStorage.getItem("token") || "";
    const decoded = decodeToken(token);

    if (agentId) {
      try {
        sessionStorage.setItem(PAYG_ACTIVE_KEY, agentId);
      } catch (_e) {}
    }

   
    params.delete("ispayg");
    const qs = params.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    localStorage.removeItem("isPayg");

    if (!decoded || !token || !agentId) return;

    (async () => {
      try {
        await axios.post(
          `${API_BASE_URL}/pay-as-you-go-saveAgent`,
          {
            customerId: decoded.customerId,
            agentId,
            status: "active",
            isFree: true,
            isByAdmin: false,
            isQuickSetup: true,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error(
          "PAYG activation error:",
          error.response?.data || error.message
        );
      }
    })();
  }, []);

  const phoneSetupSkippedRef = useRef(false);

  
  const [candidate, setCandidate] = useState(null);
  
  const [confirming, setConfirming] = useState(false);

  const [confirmError, setConfirmError] = useState("");
  
  const [editConsumed, setEditConsumed] = useState(false);

  const [showGoogleAuth, setShowGoogleAuth] = useState(false);
  const [checkingGoogle, setCheckingGoogle] = useState(false);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState("");
  const [connectedScopes, setConnectedScopes] = useState("");
  const [justReturnedFromGoogle, setJustReturnedFromGoogle] = useState(false);

  // ── Step-wise back guard ─────────────────────────────────────────
  // Device/browser back walks the wizard steps backward instead of
  // leaving the route (and never reaches the expired Stripe page). At
  // the first step, Back asks for exit confirmation → dashboard.
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  useStepBackGuard({
    // Arm immediately on mount (not gated on boot) so back can never
    // slip through to the expired Stripe page during the brief boot.
    active: true,
    // No internal back while the inline Google step is showing (basic).
    // minStepId floor hai: number locked ho to Step 2 se peeche back nahi
    // jaata — wahan se back seedha onExit (dashboard) chalata hai.
    canGoBack: () => showGoogleAuth || stepId > minStepId,
    onBack: () => {
      if (showGoogleAuth) {
        localStorage.removeItem("goLiveWizard.expectGoogleReturn");
        setShowGoogleAuth(false);
        setJustReturnedFromGoogle(false);
        return;
      }
      setStepId((s) => Math.max(minStepId, s - 1));
    },
    // On step 1 (Assign Number) there is no earlier wizard step, so Back
    // leaves the wizard. Go straight back to the dashboard (or /setup-ready) instead of
    // opening the exit-confirm dialog. (onExit only fires on step 1 — deeper
    // steps have canGoBack() true and use onBack to step backwards.)
    onExit: () =>
      navigate(isFromSetupReady ? "/setup-ready" : "/fast-agent-detail", {
        replace: true,
      }),
  });


  const [agentCtx, setAgentCtx] = useState({
    agentId: null,
    agent: null,
    business: null,
    loading: true,
  });


  /*
   * ── "Assign Number" (Step 1) ka GATE ────────────────────────────────
   *
   * Agent pe number PEHLE SE assigned hai to Step 1 pe jaana hi nahi chahiye —
   * wahan wahi number dikhta hai jo unke paas already hai, aur kuch khareedne
   * ko bhi nahi hai. Variant B me ye normal case hai: number payment se PEHLE
   * /receptionist-number pe mil jaata hai.
   *
   * Ye faisla RENDER ke waqt hota hai, kisi useEffect me nahi. Wajah:
   *   - effect timing pe depend nahi karta (agentCtx kab load hua, kaunsa
   *     effect pehle chala, StrictMode ka double-invoke — kuch farak nahi)
   *   - BACK NAVIGATION apne aap block ho jaati hai: stepId kisi bhi rastey se
   *     1 pe pahunch jaye (browser back, purana localStorage, stepper click),
   *     effectiveStepId phir bhi 2 rahega
   *
   * Number na ho to minStepId = 1 aur sab kuch bilkul pehle jaisa chalta hai.
   */
  const assignedVoipNumber = (() => {
    const raw = agentCtx?.agent?.voip_numbers;
    if (!raw) return "";
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      return first || "";
    } catch {
      return "";
    }
  })();
  // agentCtx load hone tak gate mat lagao — warna pehle render pe har agent
  // "number nahi hai" jaisa dikhta aur Step 1 flash ho jaata.
  const blockAssignStep = !agentCtx?.loading && !!assignedVoipNumber;
  // Wizard ka pehla ALLOWED step. Number locked ho to floor 1 se 2 ho jaata hai.
  const minStepId = blockAssignStep ? 2 : 1;
  // Render aur navigation dono ke liye asli step.
  const effectiveStepId = stepId < minStepId ? minStepId : stepId;

  const refreshAgentCtx = async () => {
    const agentId =
      sessionStorage.getItem("SelectAgentId") ||
      localStorage.getItem("postPurchaseAgentId") ||
      new URLSearchParams(window.location.search).get("agentId") ||
      null;

    if (!agentId) {
      setAgentCtx((c) => ({ ...c, loading: false }));
      return null;
    }

    try {
      const res = await getAgentById(agentId);
      const agent = res?.data || res || {};
      const business = agent?.business || null;
      const businessId = agent?.businessId;

      if (agent?.agent_id) sessionStorage.setItem("SelectAgentId", agent.agent_id);
      if (businessId) sessionStorage.setItem("SelectAgentBusinessId", businessId);

      try {
        sessionStorage.setItem(
          "agentDetails",
          JSON.stringify({ agent, business }),
        );
      } catch { }

      
      try {
        useAgentStore
          .getState()
          .setAgentById(agentId, { agent, business }, [], 0);
      } catch (storeErr) {
        console.warn(
          "[GoLiveWizard] zustand sync failed (non-fatal):",
          storeErr?.message || storeErr,
        );
      }

      setAgentCtx({
        agentId,
        agent,
        business,
        loading: false,
      });
      return { agent, business };
    } catch (err) {
      console.error("[GoLiveWizard] load agent failed:", err);
      setAgentCtx((c) => ({ ...c, loading: false }));
      return null;
    }
  };

  // ── Boot guard + resume ─────────────────────────────────────────────────
  useEffect(() => {
    /*
     * Add-on intro sequence — calendar OAuth round-trip resume.
     *
     * The Calendar intro's "Connect" kicks Google OAuth with
     * returnTo="/go-live-setup" (the whitelisted return path) and sets
     * the addon OAuth marker. On the way back we land here; instead of
     * booting the wizard we resume the sequence: the next queued screen
     * (Contact) if any, else the dashboard. bootChecked stays false so
     * the boot loader shows (no wizard flash) until the redirect lands.
     */
    if (isAddonOAuthReturnPending()) {
      consumeAddonOAuthReturn();
      const p = new URLSearchParams(window.location.search);
      if (p.get("googleAuth") === "success") {
        // Calendar connected through the add-on screen (/calendar-access ->
        // Google OAuth -> back here). This branch runs, and navigates away,
        // before the post-OAuth block further down that fires
        // calendar_sync_completed, so add-on calendar connects were never
        // tracked. The backend only appends googleAuth=success after a
        // successful authorisation.
        trackEvent("calendar_sync_completed", { source: "calendar_access" });
        p.delete("googleAuth");
        const rem = p.toString();
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + (rem ? `?${rem}` : ""),
        );
      }
      // The calendar OAuth was in flight → advance from "calendar".
      navigate(advanceFromKey("calendar"), { replace: true });
      return;
    }

    const token = localStorage.getItem("token");
    const agentId =
      sessionStorage.getItem("SelectAgentId") ||
      localStorage.getItem("postPurchaseAgentId") ||
      // Cold WebView / email deep-link (paidNotLive → /go-live-setup?agentId=…):
      // session + localStorage are empty, so fall back to the URL. Additive —
      // warm in-app navigation (session set) is unaffected. Mirrors the query
      // fallback the other two agentId resolvers already use (lines ~78, ~176).
      new URLSearchParams(window.location.search).get("agentId");
    const savedStep = localStorage.getItem(STORAGE_KEY);

    /*
     * A/B variant B: pehle "How Customers Reach <agent>" chuno.
     *
     * B flow me user ko wizard me seedha nahi ghusna chahiye — usse pehle tay
     * karna hai ki apna purana business number rakhna hai (call forwarding) ya
     * agent ka naya number use karna hai (seedha triage).
     *
     * Ye check YAHIN lagaya hai, wizard ke boot pe, kisi ek caller pe nahi —
     * wizard me ghusne ke kai raaste hain (Stripe ka success_url, email deep
     * link, BusinessStep). Ek jagah rok dene se sab cover ho jaate hain.
     *
     * Variant A par ye block chalta hi nahi (getVariant default "A" hai), to
     * payment-ke-baad wala purana flow bilkul waisa hi rehta hai.
     *
     * SetupPath choose hone par flag set kar deta hai, warna hum yahan se wapas
     * bhejte rehte aur loop ban jaata.
     */
    if (
      !isFromSetupReady &&
      getVariant(EXPERIMENTS.GO_LIVE_FLOW) === VARIANT_B &&
      localStorage.getItem(SETUP_PATH_CHOSEN_KEY) !== "true"
    ) {
      navigate("/setup-path", { replace: true });
      return;
    }


    if (!token || !agentId) {
      console.warn("[GoLiveWizard] Missing token or agentId — bouncing to dashboard");
      navigate("/fast-agent-detail", { replace: true });
      return;
    }

    const saved = parseInt(savedStep, 10);
    let resumeAt = 1;
    if (Number.isFinite(saved) && saved >= 1 && saved <= STEPS.length) {
      resumeAt = saved;
    }

 
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get("googleAuth") === "success";
    const expectingGoogle =
      localStorage.getItem("goLiveWizard.expectGoogleReturn") === "true";
    const returningFromGoogle = expectingGoogle && oauthSuccess;

    if (expectingGoogle && !oauthSuccess) {

      localStorage.removeItem("goLiveWizard.expectGoogleReturn");
    }

    if (oauthSuccess) {
      urlParams.delete("googleAuth");
      const remaining = urlParams.toString();
      const cleanUrl =
        window.location.pathname + (remaining ? `?${remaining}` : "");
      window.history.replaceState({}, document.title, cleanUrl);
    }

    if (returningFromGoogle) {
      setShowGoogleAuth(true);
      setJustReturnedFromGoogle(true);
    }

    (async () => {
      setStepId(resumeAt);
      if (initialStepRef.current === null) {
        initialStepRef.current = resumeAt;
      }
      await refreshAgentCtx();


      if (returningFromGoogle) {
        try {
          const tokenStr = localStorage.getItem("token") || "";
          const userId = decodeToken(tokenStr)?.id;
          if (userId) {
            const syncStatusRes = await calenderCheck(userId);
            const connected = !!syncStatusRes?.data?.connected;
            setIsGoogleConnected(connected);
            if (connected && syncStatusRes?.data?.email) {
              setConnectedEmail(syncStatusRes.data.email);
            }
            if (connected && syncStatusRes?.data?.scope) {
              setConnectedScopes(syncStatusRes.data.scope);
            }
            // GA4: calendar connected via the Go-Live / Setup-Ready add-on OAuth
            // (SetUpReady → /calendar-access → OAuth → here). The PostPurchase
            // GoogleAuthStep fires the SAME event with source "in_app"; this path
            // used to fire nothing, so calendar connects here were never tracked.
            if (connected) {
              trackEvent("calendar_sync_completed", { source: "go_live_wizard" });
            }
          }
        } catch (err) {
          console.error("[GoLiveWizard] post-OAuth sync check failed:", err);
        }
        localStorage.removeItem("goLiveWizard.expectGoogleReturn");
      }

      setBootChecked(true);
    })();
  }, [navigate]);


  useEffect(() => {
    if (!bootChecked) return;
    localStorage.setItem(STORAGE_KEY, String(stepId));
  }, [stepId, bootChecked]);

  
  const planRaw = (agentCtx?.agent?.agentPlan || "")
    .toString()
    .trim()
    .toLowerCase();
  const isFreeOrDemo = planRaw === "free" || planRaw === "demo" || planRaw === "";
  const isPaidPlan = !isFreeOrDemo;

  const CHECKOUT_GRACE_KEY = "goLiveWizard.checkoutGracePeriodStart";
  const GRACE_PERIOD_MS = 30000;
  useEffect(() => {
    if (!bootChecked) return;
    if (agentCtx?.loading) return;
    const isCheckoutReturn =
      new URLSearchParams(window.location.search).get("fromCheckout") === "1";
    if (isCheckoutReturn) return;

    try {
      const graceStart = Number(
        sessionStorage.getItem(CHECKOUT_GRACE_KEY) || "0",
      );
      if (graceStart > 0 && Date.now() - graceStart < GRACE_PERIOD_MS) {
        return;
      }
     
      if (graceStart > 0) {
        sessionStorage.removeItem(CHECKOUT_GRACE_KEY);
      }
    } catch (_e) {
    
    }

 
    if (!agentCtx?.agent) return;

   
    try {
      const paygAgent = sessionStorage.getItem(PAYG_ACTIVE_KEY);
      const currentAgentId =
        agentCtx?.agentId || sessionStorage.getItem("SelectAgentId");
      if (paygAgent && paygAgent === currentAgentId) return;
    } catch (_e) {
      
    }

    if (planRaw === "free" || planRaw === "demo") {
      console.warn(
        "[GoLiveWizard] Free/demo agent on /go-live-setup — bouncing to dashboard. plan=",
        planRaw,
      );
      navigate("/fast-agent-detail", { replace: true });
    }
  }, [bootChecked, agentCtx, planRaw, navigate]);


  // GA4 first-purchase: web checkout lands straight here (WebView passes
  // through PaymentSuccess first). Only fire when we actually returned from
  // checkout (fromCheckout=1) so a stale stash from an abandoned Stripe
  // session can't fire on an ordinary /go-live-setup visit. firePendingPurchase
  // clears the stash, so if PaymentSuccess already fired it this no-ops.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("fromCheckout") !== "1") return;
    firePendingPurchase();
  }, []);

  const checkoutUpgradeRanRef = useRef(false);
  useEffect(() => {
    if (checkoutUpgradeRanRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("fromCheckout") !== "1") return;

    const agentIdParam = params.get("agentId");
    const userIdParam = params.get("userId");
    if (!agentIdParam || !userIdParam) return;

    checkoutUpgradeRanRef.current = true;

   
    try {
      sessionStorage.setItem(CHECKOUT_GRACE_KEY, String(Date.now()));
    } catch (_e) {

    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/agent/updateFreeAgent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: userIdParam, agentId: agentIdParam }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success !== true) {
          console.warn(
            "[GoLiveWizard] updateFreeAgent did not succeed:",
            res.status,
            data
          );
        } else {
        
          localStorage.setItem("paymentDone", "true");

          const selectedPlan = (sessionStorage.getItem("selectedPlan") || "").trim().toLowerCase();
          const selectedBillingInterval = (sessionStorage.getItem("selectedBillingInterval") || "").trim().toLowerCase();
          const planTierMap = {
            starter: "starter",
            scaler: "scaler",
            growth: "growth",
          };
          const claimedFreeTrial = params.get("trial") === "1";

          try {
            await fetch(`${API_BASE_URL}/onboarding/pricing/activated`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
              },
              body: JSON.stringify({
                agentId: agentIdParam,
                planTier: claimedFreeTrial
                  ? "trial_21day"
                  : planTierMap[selectedPlan] || null,
                billingInterval: claimedFreeTrial
                  ? "monthly"
                  : selectedBillingInterval === "year"
                    ? "annual"
                    : selectedBillingInterval === "month"
                      ? "monthly"
                      : null,
                claimedFreeTrial,
              }),
            });
          } catch (lifecycleErr) {
            console.warn(
              "[GoLiveWizard] pricing activation lifecycle mark failed:",
              lifecycleErr?.message || lifecycleErr
            );
          }
        }
        // NOTE: the old subscription is NOT cancelled here any more.
        //
        // This used to POST /cancel-subscription with upgradeOldSubId, which is
        // agent.subscriptionId — and the assign-number flow overwrites that with
        // the "Add-on Services" subscription id. So an upgrade cancelled the
        // customer's $5 add-on / PAYG, zeroed their minutes, and left the real
        // Starter/Scaler plan billing. It also ran only when the browser made it
        // back here at all.
        //
        // The cancel now happens server-side in cancelSupersededSubscription,
        // off the checkout.session.completed webhook: it picks the target by
        // product (plans only, never add-ons) and stamps supersededBy so the
        // deleted-webhook does not wipe the just-paid agent.

     
        const isPaidPlanStr = (p) => {
          const v = String(p || "").trim().toLowerCase();
          return v !== "" && v !== "free" && v !== "demo";
        };
      
        let finalAgent = null;
        try {
          const maxAttempts = 10;
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const fresh = await refreshAgentCtx();
            if (fresh?.agent) finalAgent = fresh.agent;
            if (fresh && isPaidPlanStr(fresh.agent?.agentPlan)) {
              console.log(
                "[GoLiveWizard] paid plan confirmed after",
                attempt + 1,
                "refresh(es)",
              );
              try {
                sessionStorage.removeItem(CHECKOUT_GRACE_KEY);
              } catch (_e) {
                /* harmless */
              }
              break;
            }
         
            if (attempt < maxAttempts - 1) {
              await new Promise((r) => setTimeout(r, 1500));
            }
          }
        } catch (refreshErr) {
          console.warn(
            "[GoLiveWizard] refresh poll failed:",
            refreshErr?.message || refreshErr,
          );
        }
      
        try {
          const rawVoip = finalAgent?.voip_numbers;
          let hasVoip = false;
          if (Array.isArray(rawVoip)) {
            hasVoip = rawVoip.length > 0;
          } else if (typeof rawVoip === "string" && rawVoip.trim()) {
            try {
              const parsed = JSON.parse(rawVoip);
              hasVoip = Array.isArray(parsed) ? parsed.length > 0 : Boolean(parsed);
            } catch {
              hasVoip = rawVoip.trim().length > 0;
            }
          } else if (rawVoip) {
            hasVoip = true;
          }
          if (hasVoip) {
            console.log(
              "[GoLiveWizard] post-checkout agent has voip — routing to /fast-agent-detail",
            );
            try {
              sessionStorage.removeItem(CHECKOUT_GRACE_KEY);
            } catch (_e) {}
            navigate("/fast-agent-detail", { replace: true });
            return;
          }
        } catch (routeErr) {
          console.warn(
            "[GoLiveWizard] post-checkout voip routing check failed:",
            routeErr?.message || routeErr,
          );
        }
      } catch (err) {
        console.error(
          "[GoLiveWizard] post-checkout upgrade failed:",
          err?.message || err
        );
      } finally {
   
        const cleaned = new URLSearchParams(window.location.search);
        cleaned.delete("fromCheckout");
        cleaned.delete("userId");
        cleaned.delete("upgraded");
        sessionStorage.removeItem("upgradeOldSubId");
        sessionStorage.removeItem("upgradeReturnTo");
        sessionStorage.removeItem("currentAgentPlan");
        const qs = cleaned.toString();
        const next = qs
          ? `${window.location.pathname}?${qs}`
          : window.location.pathname;
        window.history.replaceState({}, document.title, next);
      }
    })();
  
  }, []);


  useEffect(() => {
    if (!bootChecked) return;
    if (agentCtx?.loading) return;
    const agent = agentCtx?.agent;
    const business = agentCtx?.business;
    const agentId = agent?.agent_id || agentCtx?.agentId;
    if (!agent || !business || !agentId) return;


    if (autoAssignTriedRef.current === agentId) return;

   
    const rawVoip = agent?.voip_numbers;
    try {
      const parsed =
        typeof rawVoip === "string" ? JSON.parse(rawVoip) : rawVoip;
      const existing = Array.isArray(parsed) ? parsed[0] : parsed;
      if (existing) {
        autoAssignTriedRef.current = agentId;
        return;
      }
    } catch {
     
    }

    let kb = {};
    try {
      kb =
        typeof business?.knowledge_base_texts === "string"
          ? JSON.parse(business.knowledge_base_texts)
          : business?.knowledge_base_texts || {};
    } catch {
      kb = {};
    }

    let addrDetails = {};
    try {
      const parsed = typeof business?.businessAddress === "string"
        ? JSON.parse(business.businessAddress)
        : business?.businessAddress || {};
      addrDetails = parsed?.addressDetails || {};
    } catch { }

    let firstBusinessAddress = {};
    try {
      const parsed = typeof business?.businessAddresses === "string"
        ? JSON.parse(business.businessAddresses)
        : business?.businessAddresses || [];
      firstBusinessAddress = Array.isArray(parsed) ? (parsed[0] || {}) : {};
    } catch { }

    const country = (business?.country_code || kb?.country_code || addrDetails?.country_code || firstBusinessAddress?.country_code || "")
      .toString()
      .toUpperCase();

    const city = business?.city || kb?.city || addrDetails?.city || firstBusinessAddress?.city || "";
    const stateCode =
      business?.state_code ||
      kb?.state_code ||
      addrDetails?.state_code ||
      firstBusinessAddress?.state_code ||
      business?.state ||
      kb?.state ||
      addrDetails?.state ||
      firstBusinessAddress?.state || "";

    console.log("[auto-assign] Start", {
      agentId,
      businessId: business?.businessId,
      rawBusiness: {
        country_code: business?.country_code,
        city: business?.city,
        state_code: business?.state_code,
        state: business?.state,
      },
      rawKB: {
        country_code: kb?.country_code,
        city: kb?.city,
        state_code: kb?.state_code,
        state: kb?.state,
      },
      rawNested: {
        addrDetailsCity: addrDetails?.city,
        firstAddrCity: firstBusinessAddress?.city,
      },
      resolved: { country, city, stateCode },
    });

   
    const isVobiz = usesVobiz() || country === "IN" || (isPythonBackend() && !isAppSumoUser());
    const resolvedCountry = isVobiz ? "IN" : country;
    const resolvedCity = isVobiz ? IN_DEFAULT_LOCATION.city : city;
    const resolvedState = isVobiz ? IN_DEFAULT_LOCATION.stateCode : stateCode;

    if (!AUTO_ASSIGN_COUNTRIES.has(resolvedCountry)) {
      console.log(
        "[auto-assign] SKIP — country not supported for auto-assign:",
        resolvedCountry || "(empty)",
        "→ user will see Step 1 manual picker",
      );
      autoAssignTriedRef.current = agentId;
      setHasTriedAutoAssign(true);
      return;
    }

    autoAssignTriedRef.current = agentId;
    setIsAutoAssigning(true);

    (async () => {
      const token = localStorage.getItem("token") || "";
      try {
        let pick = null;
        let winningAttempt = null;

        if (isVobiz) {
          console.log("[auto-assign] Attempt Vobiz (IN/Bangalore)");
          try {
            const res = await fetchAvailableVobizNumbers(
              token,
              "IN",
              resolvedCity,
              resolvedState,
              null,
              null,
            );
            const list = res?.data || [];
            console.log(
              "[auto-assign] → Vobiz returned",
              list.length,
              "numbers",
              list.length > 0 ? { sample: list[0] } : "(empty)",
            );
            if (list.length > 0) {
              pick = list[0];
              winningAttempt = "Vobiz (Bangalore, KA)";
            }
          } catch (e) {
            console.error(
              "[auto-assign] ✗ Vobiz fetch failed:",
              e?.response?.data || e?.message || e,
            );
          }
        } else {
          const attempts = [
            { label: "1) city+state+country", city: resolvedCity, state: resolvedState },
            { label: "2) state+country", city: "", state: resolvedState },
            { label: "3) country only", city: "", state: "" },
          ];

          for (const a of attempts) {
            console.log("[auto-assign] Attempt", a.label, {
              country: resolvedCountry,
              city: a.city,
              state: a.state,
            });
            try {
              const res = await fetchAvailablePhoneNumberByCountry(
                token,
                resolvedCountry,
                a.city,
                a.state,
                null,
                null,
              );
              const list = res?.data || [];
              console.log(
                "[auto-assign] →",
                a.label,
                "returned",
                list.length,
                "numbers",
                list.length > 0
                  ? { sample: list[0] }
                  : "(empty — cascading)",
              );
              if (list.length > 0) {
                pick = list[0];
                winningAttempt = a.label;
                break;
              }
            } catch (e) {
              console.error(
                "[auto-assign] ✗ fetch failed for",
                a.label,
                "-",
                e?.response?.data || e?.message || e,
              );
            }
          }
        }

        if (!pick) {
          console.warn(
            "[auto-assign] EXHAUSTED — no numbers found for country=" +
            resolvedCountry +
            ", city=" +
            resolvedCity +
            ", state=" +
            resolvedState +
            " → user will see Step 1 manual picker",
          );
          return;
        }

        console.log(
          "[auto-assign] ✓ FETCHED via",
          winningAttempt,
          {
            phone_number: pick.phone_number,
            locality: pick.locality,
            region: pick.region,
            raw: pick,
          },
        );

        setCandidate({
          phone_number: pick.phone_number,
          locality: isVobiz ? IN_DEFAULT_LOCATION.city : (pick.locality || pick.city || city || ""),
          region: isVobiz ? IN_DEFAULT_LOCATION.stateCode : (pick.region || pick.state || stateCode || ""),
        });
        console.log(
          "[auto-assign] ✓ Candidate stashed (no purchase, no DB write). Awaiting Confirm tap.",
        );
      } catch (err) {
        console.error(
          "[auto-assign] UNEXPECTED ERROR:",
          err?.response?.data || err?.message || err,
          err,
        );
      } finally {
        setIsAutoAssigning(false);
        setHasTriedAutoAssign(true);
      }
    })();
  }, [agentCtx, refreshAgentCtx, bootChecked]);


  // Upar wale assignedVoipNumber se hi — do jagah parse logic rakhne se dono
  // time ke saath alag ho jaate.
  const hasExistingNumber = () => !!assignedVoipNumber;


  const hasNumberContext = () => hasExistingNumber() || !!candidate;


  useEffect(() => {
    if (phoneSetupSkippedRef.current) return;
    if (
      bootChecked &&
      !isAutoAssigning &&
      stepId > 1 &&
      !hasNumberContext()
    ) {
      console.warn(
        "[GoLiveWizard] Invalid state: Step > 1 with no voip + no candidate. Forcing back to Step 1.",
      );
      setStepId(1);
    }
    
  }, [bootChecked, isAutoAssigning, stepId, agentCtx?.agent?.voip_numbers, candidate]);


  // Lifecycle "Finish call forwarding" nudge (paidNotLive) lands here as
  // /go-live-setup?goto=forwarding. If a number is ALREADY configured, skip the
  // Assign Number step and jump straight to the Forwarding stage (carrier →
  // forward). If there's NO number, do nothing — the normal Step-1 flow
  // (manual picker / auto-assign) provisions one first, then continues. Runs
  // once, after agentCtx (voip_numbers) has loaded.
  const gotoForwardingRef = useRef(false);
  useEffect(() => {
    if (gotoForwardingRef.current) return;
    if (!bootChecked || agentCtx?.loading) return;
    if (new URLSearchParams(window.location.search).get("goto") !== "forwarding") return;
    gotoForwardingRef.current = true;
    if (hasExistingNumber()) {
      console.log("[GoLiveWizard] goto=forwarding + number exists → skipping Assign Number to Forwarding stage");
      setStepId(2); // Forwarding visual stage begins at the Carrier step
    }
  }, [bootChecked, agentCtx?.loading, agentCtx?.agent?.voip_numbers]);

  /*
   * Number PEHLE SE assigned hai → "Assign Number" step dikhane ka koi matlab
   * nahi. Seedha Call Forwarding (Step 2) pe le jao.
   *
   * KISKE LIYE: mainly variant B. Us flow me number payment se PEHLE milta hai
   * (/receptionist-number → ReceptionistNumber.assignNumber), to payment ke baad
   * wizard ka Step 1 unhe DOBARA wahi number dikhata tha — user ko lagta tha ki
   * phir se number chunna/khareedna hai. Khareeda kuch nahi jaata tha (purchase
   * branch ImmediateOwnerShip me selectedNumber hone par waise bhi skip ho jaati
   * hai), bas ek bekaar screen thi. Ab wo screen aati hi nahi.
   *
   * Variant A pe koi asar nahi: wahan number Step 1 pe hi assign hota hai, to
   * yahan tak pahunchte waqt voip_numbers khaali hota hai aur ye block chalta
   * hi nahi.
   *
   * markSetupStep KYUN: wizard ka apna confirm-purchase ye mark karta hai
   * (dekho handleConfirmPurchase), par B flow ka /receptionist-number nahi
   * karta. Step skip karne se pehle ise mark na karein to setup score me
   * "phone number assigned" hamesha adhoora dikhta rehta.
   *
   * Ek hi baar chalta hai (ref): user manually Back dabakar apna number dekhna
   * chahe to hum use turant aage nahi phenkte.
   */
  const skipAssignStepRef = useRef(false);
  useEffect(() => {
    if (skipAssignStepRef.current) return;
    if (!blockAssignStep || stepId !== 1) return;

    skipAssignStepRef.current = true;
    // stepId ko bhi aage badha do (render to effectiveStepId se pehle hi sahi
    // hai) — taaki localStorage me lastStep 2 save ho aur reload pe wahi mile.
    setStepId(2);
    // Wizard ka apna confirm-purchase ye mark karta hai; B flow ka
    // /receptionist-number nahi karta. Bina iske setup score me "phone number
    // assigned" hamesha adhoora dikhta.
    const agentId = agentCtx?.agent?.agent_id || agentCtx?.agentId;
    if (agentId) markSetupStep("phoneNumberAssigned", agentId);
  }, [blockAssignStep, stepId, agentCtx?.agent?.agent_id, agentCtx?.agentId]);

  /*
   * Variant B: browser back → /setup-path.
   * Variant B: browser back → /setup-path (or /setup-ready if entered from setup-ready).
   *
   * Wizard ke saare steps ek hi URL (/go-live-setup) pe chalte hain, isliye
   * browser back wizard ke andar peeche nahi jaata — wo poori route hi chhod
   * deta hai aur user B flow se bahar gir jaata hai. B me uska sahi thikana
   * "How Customers Reach <agent>" hai, jahan se wo dobara rasta chun sake.
   * Agar user /setup-ready se aaya tha, to uska sahi thikana /setup-ready hai.
   *
   * Variant A me ye kuch nahi karta — wahan ka back waisa hi rehta hai.
   *
   * NOTE: hook neeche wale early returns (showGoogleAuth / !bootChecked) se
   * PEHLE call hona zaroori hai, warna react-hooks/rules-of-hooks fail hota hai.
   */
  const backTarget = isFromSetupReady ? "/setup-ready" : "/setup-path";
  const backEnabled =
    isFromSetupReady || getVariant(EXPERIMENTS.GO_LIVE_FLOW) === VARIANT_B;
  useBackTo(backTarget, backEnabled);


  const confirmAndPurchase = async (overrideCandidate) => {
    const useCandidate = overrideCandidate?.phone_number
      ? overrideCandidate
      : candidate;
    if (!useCandidate?.phone_number) {
      return { success: false, error: "No candidate number to confirm" };
    }
    const agentId = agentCtx?.agent?.agent_id || agentCtx?.agentId;
    if (!agentId) {
      return { success: false, error: "Missing agent ID" };
    }
  
    if (overrideCandidate?.phone_number) {
      setCandidate(overrideCandidate);
      setEditConsumed(true);
    }
    setConfirming(true);
    setConfirmError("");
    try {
      const env = process.env.REACT_APP_ENV;
      const bizCountry = (
        agentCtx?.business?.country_code ||
        agentCtx?.business?.country ||
        ""
      ).toString().toUpperCase();
      const isVobiz =
        usesVobiz() ||
        bizCountry === "IN" ||
        (useCandidate?.phone_number && useCandidate.phone_number.startsWith("+91")) ||
        (isPythonBackend() && !isAppSumoUser());
      if (env === "production") {
        console.log(
          `[confirm-purchase] Production env — placing ${isVobiz ? "Vobiz" : "Telnyx"} order for`,
          useCandidate.phone_number,
        );
        let orderRes;
        if (isVobiz) {
          const { createVobizNumberOrder } = await import("../../Store/apiStore");
          orderRes = await createVobizNumberOrder(useCandidate.phone_number, agentId);
        } else {
          const { createNumberOrder } = await import("../../Store/apiStore");
          orderRes = await createNumberOrder(useCandidate.phone_number, agentId);
        }
        if (orderRes && orderRes.success === false) {
          throw new Error(orderRes.message || "Could not purchase the number");
        }
        console.log(`[confirm-purchase] ✓ ${isVobiz ? "Vobiz" : "Telnyx"} order completed`);
      } else {
        console.log(
          "[confirm-purchase] Non-production env (" +
          (env || "undefined") +
          `) — SKIPPING ${isVobiz ? "Vobiz" : "Telnyx"} purchase, only updating DB`,
        );
      }

      const updatePayload = {
        voip_numbers: [useCandidate.phone_number],
        voip_numbers_created: new Date(),
        voip_number_city: useCandidate.locality || "",
        voip_number_state: useCandidate.region || "",
        assignNumFree: 1,
        isNumActivated: 1,
        isDeactivated: false,
        agentStatus: true,
      };
      console.log("[confirm-purchase] updateAgent payload:", updatePayload);
      await updateAgent(agentId, updatePayload);
      markSetupStep("phoneNumberAssigned", agentId);
      trackEvent("number_assigned");
      
      // "Number assigned successfully!" toast ka nishaan (CarrierSelectStep ise
      // padh kar toast dikhata hai). Number yahan tak pahunchte hi assign ho chuka
      // hai (upar updateAgent safal), isliye hamesha lagao. Pehle ye sirf tab lagta
      // tha jab asli kharid hui ho (production) — dev/staging me kharid skip hoti
      // hai, to number assign hone par bhi toast nahi aata tha.
      try {
        sessionStorage.setItem("goLiveWizard.numberAssignedToast", "1");
      } catch { }
      console.log("[confirm-purchase] ✓ DB updated & score marked");

      await refreshAgentCtx();
     
      setCandidate(null);
      setConfirming(false);
      return { success: true };
    } catch (err) {
      console.error(
        "[confirm-purchase] ✗ failed:",
        err?.response?.data || err?.message || err,
      );
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't reserve this number. Please try picking a different one.";
      setConfirmError(msg);
      setConfirming(false);
      return { success: false, error: msg };
    }
  };


  const goToVisualStep = (visualNum) => {
    const targetInternal = VISUAL_FIRST_INTERNAL[visualNum];
    if (!targetInternal) return;
    // Assign Number locked hai to stepper ka pehla circle dabane se bhi
    // wahan nahi jaana.
    if (targetInternal < minStepId) return;
    if (targetInternal === stepId) return;
    setStepId(targetInternal);
  };

 
  const finishToContactSync = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem("onboardComplete", "true");
   
    let assignOnly = false;
    try {
      assignOnly =
        sessionStorage.getItem("goLiveWizard.assignNumberOnly") === "1";
      if (assignOnly) {
        sessionStorage.removeItem("goLiveWizard.assignNumberOnly");
      }
    } catch (_e) {}
    if (assignOnly) {
      navigate("/fast-agent-detail", { replace: true });
      return;
    }
    navigate("/contact-sync", { replace: true });
  };

  
  if (showGoogleAuth) {
    return (
      <div className={styles.wizard}>
        <main className={`${styles.body} ${styles.bodyGoogleAuth}`}>
          <GoogleAuthStep
            isConnected={isGoogleConnected}
            connectedEmail={connectedEmail}
            connectedScopes={connectedScopes}
            justReturnedFromGoogle={justReturnedFromGoogle}
            onNext={finishToContactSync}
            onSkip={finishToContactSync}
            showSkipLink={true}
            returnToPath="/go-live-setup"
            expectReturnKey="goLiveWizard.expectGoogleReturn"
            lastStepKey="goLiveWizard.lastStep"
            onBackToPrevious={() => {
              setShowGoogleAuth(false);
              setJustReturnedFromGoogle(false);
            }}
          />
        </main>
      </div>
    );
  }

  if (!bootChecked) {
    return (
      <div className={styles.bootLoader}>
        <span className={styles.spinner} />
      </div>
    );
  }



  /*
   * ── Setup Ready forward exits ───────────────────────────────────────
   *
   * `isFeaturePending` answers "does this feature still need doing?" and
   * deliberately answers YES whenever we do not know (score not resolved, fetch
   * failed). Unknown must never shorten the ladder.
   *
   * Forwarding mirrors /setup-ready's own derivation — the score step OR the
   * agent record's `callForwarding` — so the two screens cannot disagree about
   * the card the user just tapped.
   */
  const isFeaturePending = (feature) => {
    if (!setupReadyDone) return true;
    if (feature === "forwarding") {
      return !(setupReadyDone.forwarding || !!agentCtx?.agent?.callForwarding);
    }
    if (feature === "triage") return !setupReadyDone.triage;
    return true;
  };

  /*
   * The next internal WIZARD step that still needs the user, looking only at
   * what lies AFTER `fromStep`. `null` does NOT mean "done" — it means the
   * wizard has nothing left; the ladder continues into the add-on sequence.
   */
  const nextPendingStep = (fromStep) => {
    if (fromStep < 2 && isFeaturePending("forwarding")) return 2;
    if (fromStep < 4 && isFeaturePending("triage")) return 4;
    return null;
  };

  /*
   * Hand off to the add-on sequence — the step the ladder continues with once
   * the wizard is out of pending steps.
   *
   * Triage is what normally launches this (`handleAddonExitTo`), so hopping over
   * an already-configured Triage skipped the add-ons with it: a user who fixed
   * only Call Forwarding was returned to /setup-ready without ever being offered
   * contact sync. Handing off here restores that link.
   *
   * Nothing is pre-filtered out of the sequence. Each add-on screen judges its
   * own feature with its own source of truth and self-skips when it is already
   * done, so this adds no second definition of "configured". When every screen
   * self-skips, `advanceFromKey` finishes the run on /setup-ready anyway.
   *
   * agentId is forced into the search: Triage is reached with it on the URL, but
   * a /setup-ready entry often is not, and `cleanSearch` only forwards what it
   * is given.
   */
  const handOffToAddonSequence = () => {
    const params = new URLSearchParams(location.search || "");
    if (!params.get("agentId")) {
      let id = "";
      try {
        id = sessionStorage.getItem("SelectAgentId") || agentCtx?.agentId || "";
      } catch (_e) {
        id = agentCtx?.agentId || "";
      }
      if (id) params.set("agentId", id);
    }
    navigate(beginAddonSequence(["contact", "calendar"], params.toString()));
  };

  /*
   * Advance out of the feature the user is on. Returns true when it handled the
   * move (advanced past a completed feature, or handed off), false when the
   * caller should fall through to its normal behaviour.
   *
   * Only called when `isFromSetupReady` — the plain onboarding ladder never
   * reaches this and is byte-for-byte unchanged.
   */
  const advancePastFeature = (fromStep) => {
    const target = nextPendingStep(fromStep);
    if (target === null) {
      handOffToAddonSequence();
      return true;
    }
    if (target === 2) requestDeviceCarrier();
    setStepId(target);
    return true;
  };

  // CarrierSelectStep calls onNext(carrier, conditions) once the "When Call
  // Forwarding?" sheet is confirmed; every other step calls onNext() bare.
  const goNext = async (_carrier, conditions) => {
    if (Array.isArray(conditions)) setForwardingConditions(conditions);

    if (effectiveStepId < STEPS.length) {
      /*
       * Entered from /setup-ready and standing on the LAST step of the feature
       * that was tapped: hop over anything already configured, and leave for
       * /setup-ready when nothing downstream is pending. Steps inside a feature
       * (Carrier → Forwarding codes) fall through and advance normally.
       */
      if (isFromSetupReady && FEATURE_END_STEP[stepId] === stepId) {
        if (advancePastFeature(stepId)) return;
      }
      /*
       * Carrier step pe ja rahe hain → native shell se device ka carrier maang
       * lo, taaki agli screen pe user ko wo pehle se chuna hua mile.
       *
       * Ye flow A ka rasta hai (user "Continue with this number" dabata hai aur
       * step 1 → 2 jaata hai). Flow B is step se guzarta hi nahi — wahan yahi
       * request SetupPath ke Continue par jaati hai.
       *
       * Jawab ka intezaar nahi karte: native "message" event me baad me bhejta
       * hai aur wo sessionStorage me chala jaata hai; SelectCarrier subscribe
       * karke use utha leti hai. Web pe ye chup-chaap no-op hai.
       */
      if (effectiveStepId + 1 === 2) requestDeviceCarrier();
      setStepId(effectiveStepId + 1);
      return;
    }

    /*
     * Assign-number-only / single-feature visit (Setup Summary popup, dashboard
     * Assign Number CTA): the user came here to do ONE thing, not to run the
     * onboarding ladder. Hand straight to finishToContactSync, which reads the
     * same flag and returns them to /fast-agent-detail.
     *
     * Without this the calenderCheck below runs and, whenever the calendar is
     * not connected, shows GoogleAuthStep ("Let your agent work with Google") —
     * an onboarding add-on screen that has no business appearing in an isolated
     * single-feature visit.
     */
    let assignOnlyVisit = false;
    try {
      assignOnlyVisit =
        sessionStorage.getItem("goLiveWizard.assignNumberOnly") === "1";
    } catch (_e) {
      /* storage unavailable — fall through to the normal path */
    }
    if (assignOnlyVisit) {
      finishToContactSync();
      return;
    }

    setCheckingGoogle(true);
    try {
      const tokenStr = localStorage.getItem("token") || "";
      const userId = decodeToken(tokenStr)?.id;
      if (!userId) {
        finishToContactSync();
        return;
      }
      const syncStatusRes = await calenderCheck(userId);
      const connected = !!syncStatusRes?.data?.connected;
      if (connected) {
       
        finishToContactSync();
        return;
      }
  
      setIsGoogleConnected(false);
      setConnectedEmail("");
      setConnectedScopes("");
      setJustReturnedFromGoogle(false);
      setShowGoogleAuth(true);
    } catch (err) {
      console.error(
        "[GoLiveWizard] calenderCheck failed, skipping Google screen:",
        err?.message || err,
      );
      finishToContactSync();
    } finally {
      setCheckingGoogle(false);
    }
  };

  const goBack = () => {
    // minStepId floor — number locked ho to Step 2 se peeche nahi.
    if (effectiveStepId > minStepId) setStepId(effectiveStepId - 1);
    if (isFromSetupReady && stepId <= (initialStepRef.current || 1)) {
      navigate("/setup-ready", { replace: true });
      return;
    }
    if (stepId > 1) setStepId((s) => s - 1);
    else if (isFromSetupReady) {
      navigate("/setup-ready", { replace: true });
    }
  };


  
  const goSkip = () => {
    if (effectiveStepId === 2) {
      trackEvent("call_forwarding_skipped", { step: "carrier" });
      /*
       * Skip abandons the WHOLE forwarding feature (Carrier + Forwarding
       * codes), so we look at what comes after step 3, not after step 2.
       * Same rule as Save: hop over what's done, exit when nothing is left.
       */
      if (isFromSetupReady && advancePastFeature(3)) return;
      setStepId(4);
      return;
    }
  };


  const skipPhoneSetup = () => {
    trackEvent("call_forwarding_skipped", { step: "phone_setup" });
    phoneSetupSkippedRef.current = true;
    // "Use the agent's number instead" skips assignment AND forwarding, so the
    // look-ahead starts after step 3 here too.
    if (isFromSetupReady && advancePastFeature(3)) return;
    setStepId(4);
  };

  const current = STEPS.find((s) => s.id === effectiveStepId) || STEPS[0];
  const StepComponent = current.Component;


  const onStep1 = effectiveStepId === 1;
  const stillResolvingNumber =
    onStep1 &&
    (agentCtx?.loading ||
      isAutoAssigning ||
      (!hasNumberContext() && !hasTriedAutoAssign));

  
  const currentVisualStep = current.visualStep || 1;

 

  /*
   * Stepper sirf variant A me.
   *
   * A me user poora wizard step 1 se chalta hai, to "Assign Number → Call
   * Forwarding → Call Triage" sahi kahani batata hai.
   *
   * B me number bahut pehle /receptionist-number pe assign ho chuka hota hai,
   * aur user yahan seedha step 2 ya 4 se ghusta hai (SetupPath ke choice se).
   * Aisa stepper jhoot bolta hai — pehla step pehle se tick, aur user ne wo
   * screen dekhi hi nahi. Isliye B me chhupa dete hain.
   */
  const showStepper = getVariant(EXPERIMENTS.GO_LIVE_FLOW) !== VARIANT_B;

  return (
    <div className={styles.wizard}>
      {showStepper && (
        <TopStepper
          currentVisualStep={currentVisualStep}
          onStepClick={goToVisualStep}
        />
      )}
      <main className={styles.body}>
        {stillResolvingNumber ? (
          <div className={styles.numberResolver}>
            <span className={styles.spinner} aria-hidden="true" />
            <span className={styles.numberResolverText}>
              Finding the best number for your business…
            </span>
          </div>
        ) : (
          <StepComponent
            stepId={effectiveStepId}
            totalSteps={STEPS.length}
            onNext={goNext}
            onBack={goBack}
            onSkip={goSkip}
            agentCtx={agentCtx}
            refreshAgentCtx={refreshAgentCtx}
            /* GoLive-only: route the "Sophia is live" dialog's add-on
               cards + Get Add-Ons through the new full-screen intro
               nudges (/screen-your-calls, /calendar-access). PostPurchase
               omits this prop and keeps its inline ContactSync handoff. */
            useAddonIntro={true}
            candidate={candidate}
            isPaidPlan={isPaidPlan}
            confirming={confirming}
            confirmError={confirmError}
            onConfirmPurchase={confirmAndPurchase}
            editConsumed={editConsumed}
            wizardInlinePicker={onStep1}
         
            onSkipPhoneSetup={skipPhoneSetup}
           
            wizardSkipPublishDirectly={effectiveStepId === 2}
            /* Conditional call forwarding — the sheet only appears on the
               Carrier step; the Forwarding step reads the resulting
               selection to decide single-code vs multi-code layout. */
            conditionPrompt={effectiveStepId === 2}
            forwardingConditions={forwardingConditions}
          />
        )}
      </main>

      <ExitConfirmModal
        open={exitConfirmOpen}
        onStay={() => setExitConfirmOpen(false)}
        onLeave={() => {
          setExitConfirmOpen(false);
          navigate("/fast-agent-detail", { replace: true });
        }}
      />
    </div>
  );
}
